export type EventoPermissaoUser = {
  Master?: boolean | null;
  logins?: Array<{ Id?: number }> | null;
  usuarioLogins?: Array<{
    tipoPermissao?: string;
    loginsId?: number;
    logins?: { Id?: number } | null;
  }> | null;
  usuarioConfiguracaos?: Array<{
    configuracaosId?: number;
    ConfiguracaosId?: number;
    configuracaos?: { Id?: number } | null;
  }> | null;
};

export type EventoPermissaoEvento = {
  ConfiguracaoId?: number | null;
  configuracaos?: { Id?: number } | unknown | null;
  logins?: { usuarioLogins?: unknown[] | null } | null;
  usuarioEventos?: Array<{ permissao?: string } | null> | null;
  equipanteeventos?: Array<{ Tipo?: number; padrinhos?: unknown } | null> | null;
  circulos?: unknown[] | null;
};

function configIdDoEvento(evento: EventoPermissaoEvento): number | undefined {
  if (evento.ConfiguracaoId && evento.ConfiguracaoId > 0) {
    return evento.ConfiguracaoId;
  }
  const cfg = evento.configuracaos;
  if (cfg && typeof cfg === "object" && "Id" in cfg) {
    const id = (cfg as { Id?: number }).Id;
    if (id && id > 0) return id;
  }
  return undefined;
}

/** Admin da configuração daquele evento — não basta o payload trazer `configuracaos`. */
export function userAdministersEventoConfig(
  user: EventoPermissaoUser | null | undefined,
  evento: EventoPermissaoEvento | null | undefined,
): boolean {
  if (!user || !evento) return false;
  const configId = configIdDoEvento(evento);
  if (!configId) return false;
  return (
    user.usuarioConfiguracaos?.some(
      (uc) =>
        uc?.configuracaos?.Id === configId ||
        uc?.configuracaosId === configId ||
        uc?.ConfiguracaosId === configId,
    ) ?? false
  );
}

export function getEventoPermissoes(
  user: EventoPermissaoUser | null | undefined,
  evento: EventoPermissaoEvento | null | undefined,
): string[] {
  const array: string[] = [];
  if (!user || !evento) return array;

  if (
    user.Master ||
    (evento.logins?.usuarioLogins?.length ?? 0) > 0 ||
    userAdministersEventoConfig(user, evento)
  ) {
    array.push("Organizador");
  }

  if ((evento.usuarioEventos?.length ?? 0) > 0 || array.includes("Organizador")) {
    for (const ue of evento.usuarioEventos ?? []) {
      const p = ue?.permissao;
      if (!p || array.includes(p)) continue;
      if (
        p === "Coordenador" &&
        !evento.equipanteeventos?.some((e) => e?.Tipo === 1)
      ) {
        continue;
      }
      array.push(p);
    }

    if (evento.equipanteeventos?.some((e) => e?.padrinhos)) {
      array.push("Padrinho");
    }
    if ((evento.circulos?.length ?? 0) > 0) {
      array.push("Dirigente");
    }
  }

  if (
    evento.equipanteeventos?.some((e) => e?.Tipo === 1) &&
    !array.includes("Coordenador")
  ) {
    array.push("Coordenador");
  }

  return array;
}

export function hasParticipantesListPermission(permissoes: string[]): boolean {
  return (
    permissoes.includes("Organizador") ||
    permissoes.includes("Total") ||
    permissoes.includes("Inscrições") ||
    permissoes.includes("Financeiro") ||
    permissoes.includes("Padrinho")
  );
}

/** Visão operacional: usuário é dirigente/responsável de um círculo no evento selecionado. */
export function hasDirigentePermission(permissoes: string[]): boolean {
  return permissoes.includes("Dirigente");
}

/** Listagem e abas administrativas de voluntários (equipes, quartos, ata). */
export function hasVoluntariosListPermission(permissoes: string[]): boolean {
  return (
    permissoes.includes("Organizador") ||
    permissoes.includes("Total") ||
    permissoes.includes("Inscrições")
  );
}

/** Visão operacional: usuário é coordenador de equipe no evento selecionado. */
export function hasCoordenadorPermission(permissoes: string[]): boolean {
  return permissoes.includes("Coordenador");
}

export function hasEventoResumoPermission(permissoes: string[]): boolean {
  return (
    permissoes.includes("Organizador") ||
    permissoes.includes("Total") ||
    permissoes.includes("Financeiro") ||
    permissoes.includes("Inscrições")
  );
}

export function hasCheckinPermission(permissoes: string[]): boolean {
  return ["Organizador", "Total", "Financeiro", "Inscrições", "Check-in"].some((role) => permissoes.includes(role));
}

/** Operadores dedicados não herdam as ações administrativas do perfil completo. */
export function isCheckinOnlyPermission(permissoes: string[]): boolean {
  return permissoes.includes("Check-in") && !["Organizador", "Total", "Financeiro", "Inscrições", "Padrinho"].some((role) => permissoes.includes(role));
}

export {
  canAccessIgrejaMode,
  hasIgrejaArea,
  isIgrejaAdm,
} from "./igreja-permissoes";

/** Home/coordenador/dirigente podem manter o evento selecionado fora da lista da tela. */
export function rolesAllowStickySelectedEvent(roles: string[]): boolean {
  return (
    roles.includes("Home") ||
    roles.includes("Coordenador") ||
    roles.includes("Dirigente")
  );
}
